import { useTranslation } from 'react-i18next';
import { Wrench, Package, ShieldCheck, Lightbulb, RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { JobPrepResponse } from '@/api/types';

interface JobPrepViewProps {
  data: JobPrepResponse;
  onRegenerate: () => void;
  isRegenerating: boolean;
}

export function JobPrepView({ data, onRegenerate, isRegenerating }: Readonly<JobPrepViewProps>) {
  const { t } = useTranslation();

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{t('jobPrep.title')}</h3>
        <Button variant="ghost" size="sm" onClick={onRegenerate} disabled={isRegenerating}>
          {isRegenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-1">{t('jobPrep.regenerate')}</span>
        </Button>
      </div>

      {data.tools.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Wrench className="h-4 w-4" />
              {t('jobPrep.tools')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.tools.map((tool) => (
              <div key={tool.name} className="flex items-center justify-between text-sm">
                <span>{tool.name}</span>
                <div className="flex items-center gap-2">
                  {tool.note && <span className="text-muted-foreground text-xs">{tool.note}</span>}
                  <Badge variant="secondary">
                    {t('jobPrep.toolQuantity', { count: tool.quantity })}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.materials.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Package className="h-4 w-4" />
              {t('jobPrep.materials')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.materials.map((mat) => (
              <div key={mat.name} className="flex items-center justify-between text-sm">
                <span>{mat.name}</span>
                <div className="flex items-center gap-2">
                  {mat.note && <span className="text-muted-foreground text-xs">{mat.note}</span>}
                  <span className="text-muted-foreground text-xs">{mat.quantity}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.safety.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4" />
              {t('jobPrep.safety')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.safety.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <span>{item.name}</span>
                {item.note && <span className="text-muted-foreground text-xs">{item.note}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.tips.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Lightbulb className="h-4 w-4" />
              {t('jobPrep.tips')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-inside list-decimal space-y-1 text-sm">
              {data.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
